import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/**
 * Slot shapes used across the booking frequency flow:
 *
 * "raw slot"       – what /api/student/calendar returns and what the sidebar keeps in
 *                    selectedBookingSlotsList: { slotId, studentSlotDate, studentSlotStartTime,
 *                    studentSlotEndTime, teacherSlotDate, teacherSlotStartTime, ... }
 * "canonical slot" – what every screen below renders and what the schedule API needs:
 *                    { slotDateInTimeZone, slotStartTimeInTimeZone, slotEndTimeInTimeZone,
 *                      teacherSlotInfoId }
 *
 * Week bucketing always runs on raw slots (calendar selection + preview-schedule response),
 * mapping to canonical happens right before a screen renders or a payload is built.
 */

/**
 * The only repeat lengths we offer, on top of "book everything". A bucket holds
 * `weeks x cadence` classes, where cadence is how many slots the student picked in
 * one week — so a 2-a-week pattern reads 2 / 8 / 16, a 1-a-week pattern 1 / 4 / 8.
 */
const WEEK_BUCKETS = [1, 4, 8];

const WEEK_OPTION_IDS = {
  1: '1week',
  4: '4weeks',
  8: '8weeks',
};

export const WEEK_COUNT_BY_OPTION = {
  '1week': 1,
  '4weeks': 4,
  '8weeks': 8,
};

const formatWeeksTitle = (weeks) =>
  weeks <= 1 ? 'Book only for this week' : `Book for ${weeks} weeks`;

const formatClassesLabel = (count) =>
  `${count} ${count === 1 ? 'class' : 'classes'}`;

// Spell out how many classes "book all" actually books, e.g. "Book All 6 Classes".
export const formatBookAllTitle = (count) =>
  count > 0 ? `Book All ${count} ${count === 1 ? 'Class' : 'Classes'}` : 'Book All Classes';

const getRecurringSlotDate = (slot) => dayjs(slot.studentSlotDate);

const getFirstRecurringDate = (slots = []) => {
  if (!slots.length) return null;
  return slots.reduce((min, slot) => {
    const date = getRecurringSlotDate(slot);
    return date.isBefore(min, 'day') ? date : min;
  }, getRecurringSlotDate(slots[0]));
};

export const getSlotsForWeeks = (slots = [], weeks = 1) => {
  const firstDate = getFirstRecurringDate(slots);
  if (!firstDate || !weeks) return [];

  const endExclusive = firstDate.add(weeks, 'week');

  return slots.filter((slot) => {
    const date = getRecurringSlotDate(slot);
    return !date.isBefore(firstDate, 'day') && date.isBefore(endExclusive, 'day');
  });
};

/**
 * Classes per week the student actually asked for — the slot count inside the first
 * rolling 7-day window (anchored on the earliest slot, not on a calendar Monday).
 */
export const getWeeklyCadence = (slots = []) => getSlotsForWeeks(slots, 1).length;

/** How many weeks a bucket really covers, so a short bucket can say "3 weeks" not "4". */
const getWeeksCovered = (slots = [], firstDate) => {
  if (!slots.length || !firstDate) return 0;

  const lastDate = slots.reduce((max, slot) => {
    const date = getRecurringSlotDate(slot);
    return date.isAfter(max, 'day') ? date : max;
  }, getRecurringSlotDate(slots[0]));

  return Math.floor(lastDate.diff(firstDate, 'day') / 7) + 1;
};

/** raw slots -> canonical slots (drops anything without a date/time/id) */
export const mapRecurringSlots = (slots = []) =>
  slots
    .map((slot) => {
      if (!slot?.studentSlotDate || !slot?.studentSlotStartTime || !slot?.slotId) {
        return null;
      }

      const studentLocalDate = slot.studentLocalDate
        ? dayjs(slot.studentLocalDate).isValid()
          ? dayjs(slot.studentLocalDate).format('DD/MM/YYYY')
          : undefined
        : undefined;
      const studentLocalStart = slot.studentLocalStartTime
        ? dayjs(slot.studentLocalStartTime, 'HH:mm:ss').isValid()
          ? dayjs(slot.studentLocalStartTime, 'HH:mm:ss').format('HH:mm')
          : undefined
        : undefined;
      const studentLocalEnd = slot.studentLocalEndTime
        ? dayjs(slot.studentLocalEndTime, 'HH:mm:ss').isValid()
          ? dayjs(slot.studentLocalEndTime, 'HH:mm:ss').format('HH:mm')
          : undefined
        : undefined;

      return {
        slotDateInTimeZone: dayjs(slot.studentSlotDate).format('DD/MM/YYYY'),
        slotStartTimeInTimeZone: dayjs(slot.studentSlotStartTime, 'HH:mm:ss').format('HH:mm'),
        slotEndTimeInTimeZone: slot.studentSlotEndTime
          ? dayjs(slot.studentSlotEndTime, 'HH:mm:ss').format('HH:mm')
          : undefined,
        teacherSlotInfoId: slot.slotId,
        ...(slot.classNumber ? { classNumber: slot.classNumber } : {}),
        ...(studentLocalDate ? { studentDateInTimeZone: studentLocalDate } : {}),
        ...(studentLocalStart ? { studentStartTimeInTimeZone: studentLocalStart } : {}),
        ...(studentLocalEnd ? { studentEndTimeInTimeZone: studentLocalEnd } : {}),
      };
    })
    .filter(Boolean);

const formatTimeForPreview = (time = '') => {
  const [hours = '', minutes = '', seconds = '00'] = String(time).split(':');
  if (!hours || !minutes) return '';
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
};

/**
 * Payload for ONBOARDING_APIS.previewSchedule — the API that returns the recurring
 * (weekly repeat) slots for the pattern the teacher just picked.
 * studentId is the student record id (selectedChatData.studentIdForCalendar), NOT the user id.
 */
export const buildPreviewSchedulePayload = (canonicalSlots = [], context = {}) => {
  const sortedSlots = canonicalSlots
    .map((slot) => {
      const date = dayjs(slot?.slotDateInTimeZone, 'DD/MM/YYYY');
      const time = formatTimeForPreview(slot?.slotStartTimeInTimeZone);

      if (!date.isValid() || !time) return null;

      return {
        date: date.format('YYYY-MM-DD'),
        day: date.format('dddd').toUpperCase(),
        time,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      return dateCompare || a.time.localeCompare(b.time);
    });

  if (!sortedSlots.length) return null;

  return {
    studentId: Number(context?.studentId),
    teacherUserId: String(context?.teacherUserId ?? ''),
    courseId: String(context?.courseId ?? ''),
    matchedSlots: sortedSlots.map(({ day, time }) => ({
      day,
      time,
      timeZone: 'IST',
    })),
    startDate: sortedSlots[0].date,
    scheduleType: 'fixed',
  };
};

/** preview-schedule response -> raw slots */
export const mapPreviewScheduleDates = (dates = []) =>
  dates.flatMap((dateEntry) =>
    (dateEntry?.slots || []).map((slot) => ({
      // Primary slot times from API (used for booking + main label)
      studentSlotDate: dateEntry.date,
      studentSlotStartTime: slot.startTime,
      studentSlotEndTime: slot.endTime,
      slotId: slot.teacherSlotInfoId,
      // Student-local times from preview-schedule (for display only)
      studentLocalDate: dateEntry.studentDate || null,
      studentLocalStartTime: slot.studentStartTime || null,
      studentLocalEndTime: slot.studentEndTime || null,
    }))
  );

export const sortRawSlots = (slots = []) =>
  [...slots].sort((a, b) => {
    const dateCompare = dayjs(a.studentSlotDate).diff(dayjs(b.studentSlotDate));
    if (dateCompare !== 0) return dateCompare;
    return dayjs(a.studentSlotStartTime, 'HH:mm:ss').diff(
      dayjs(b.studentSlotStartTime, 'HH:mm:ss')
    );
  });

/**
 * Both ids that mean "book every slot the teacher has open" — 'all' when that finishes
 * the pack, 'max' when it does not. Only 'all' is a fixed weekly schedule; 'max' is a
 * partial run, so callers book it as flexible.
 */
export const isBookEverythingOption = (optionId) => optionId === 'all' || optionId === 'max';

export const buildFrequencyOptions = (recurringSlots = [], remainingClasses) => {
  const totalSlots = recurringSlots.length;
  if (!totalSlots) return [];

  const remainingCount = Number(remainingClasses);
  // Whether the slots on offer are enough to finish scheduling the student's pack.
  const coversWholePack =
    !Number.isFinite(remainingCount) || remainingCount <= totalSlots;

  const firstDate = getFirstRecurringDate(recurringSlots);
  const cadence = getWeeklyCadence(recurringSlots);

  const options = [];

  // Booking the whole pack leads: it is the recommended, fixed weekly schedule.
  if (coversWholePack) {
    options.push({
      id: 'all',
      title: formatBookAllTitle(totalSlots),
      subtitle: 'Keep the student on a weekly routine',
      highlights: [
        'Teacher reserved for the student\'s timing.',
        'Recommended for consistent progress.',
        'Student can pause classes anytime.',
      ],
      showBestForYou: true,
      slotCount: totalSlots,
    });
  }

  // A bucket that lands on the same class count as the longest run (or as an earlier,
  // shorter bucket) is the same booking twice — keep only the first.
  const seenCounts = new Set([totalSlots]);

  WEEK_BUCKETS.forEach((weeks) => {
    // Asking for more classes than exist means this bucket IS the longest run.
    if (cadence * weeks >= totalSlots) return;

    const weekSlots = getSlotsForWeeks(recurringSlots, weeks);
    if (!weekSlots.length || seenCounts.has(weekSlots.length)) return;
    seenCounts.add(weekSlots.length);

    // Teacher availability can be patchy, so a bucket may hold fewer classes or span
    // fewer weeks than the nominal length — label it with what it actually books.
    const weeksCovered = Math.min(getWeeksCovered(weekSlots, firstDate), weeks);

    options.push({
      id: WEEK_OPTION_IDS[weeks],
      title: formatWeeksTitle(weeksCovered),
      weeks,
      classes: formatClassesLabel(weekSlots.length),
      slotCount: weekSlots.length,
    });
  });

  // When the teacher cannot cover the pack there is no "book all" to offer. Every slot
  // they do have is still bookable, but as an unbadged N-week run at the end of the
  // list — it books more classes than the buckets above, yet finishes nothing.
  if (!coversWholePack) {
    options.push({
      id: 'max',
      title: formatWeeksTitle(getWeeksCovered(recurringSlots, firstDate)),
      // subtitle: `All your teacher has open for now — ${remainingCount - totalSlots} of your ${remainingCount} classes can be booked later`,
      classes: formatClassesLabel(totalSlots),
      showBestForYou: true,
      slotCount: totalSlots,
    });
  }

  return options;
};

/**
 * The option a screen lands on before the student touches anything: the recommended
 * one — "book all" at the top when it is offered, otherwise the longest run at the
 * bottom — falling back to the first card if nothing is badged.
 */
export const getDefaultFrequencyOptionId = (options = []) =>
  options.find((option) => option.showBestForYou)?.id || options[0]?.id || '';

export const getTimezoneLabel = (timezone) => {
  const tz = String(timezone || 'Asia/Kolkata').trim();
  if (!tz) return 'IST';
  // Short codes from preview-schedule (e.g. GMT, IST, EST) — show as returned
  if (!tz.includes('/')) return tz;
  if (tz.includes('Kolkata') || tz.includes('Calcutta')) return 'IST';
  const parts = tz.split('/');
  return parts[parts.length - 1]?.replace(/_/g, ' ') || 'IST';
};

export const formatSlotLabel = (slot, timezoneLabel) => {
  const date = dayjs(slot.slotDateInTimeZone, 'DD/MM/YYYY').format('ddd, D MMMM');
  const time = dayjs(slot.slotStartTimeInTimeZone, 'HH:mm').format('hh:mm A');
  return `${date}, ${time}`;
};

/** Student-local time line from preview-schedule studentDate / studentStartTime. */
export const formatStudentTimezoneSlotLabel = (slot, studentTimezoneLabel) => {
  if (!slot?.studentDateInTimeZone || !slot?.studentStartTimeInTimeZone) return '';
  const date = dayjs(slot.studentDateInTimeZone, 'DD/MM/YYYY');
  const time = dayjs(slot.studentStartTimeInTimeZone, 'HH:mm');
  if (!date.isValid() || !time.isValid()) return '';
  const label = studentTimezoneLabel ? ` ${studentTimezoneLabel}` : '';
  return `${date.format('ddd, D MMMM')}, ${time.format('hh:mm A')}${label}`;
};

/**
 * Teacher-timezone line for a slot, taken from the calendar data the sidebar already
 * loaded (keyed by slotId). Recurring slots outside the loaded month have no entry,
 * in which case nothing is shown rather than a guessed time.
 */
export const formatTeacherSlotLabel = (slot, slotLookup = {}) => {
  const rawSlot = slotLookup?.[String(slot?.teacherSlotInfoId)];
  if (!rawSlot?.teacherSlotDate || !rawSlot?.teacherSlotStartTime) return '';

  const date = dayjs(rawSlot.teacherSlotDate, 'YYYY-MM-DD').format('ddd, D MMMM');
  const time = dayjs(rawSlot.teacherSlotStartTime, 'HH:mm:ss').format('hh:mm A');
  return `${date}, ${time}`;
};

const getSlotDateRangeDays = (canonicalSlots = []) => {
  const dates = canonicalSlots
    .map((slot) => dayjs(slot.slotDateInTimeZone, 'DD/MM/YYYY'))
    .filter((date) => date.isValid());

  if (dates.length < 2) return 0;

  const firstDate = dates.reduce((min, date) => (date.isBefore(min) ? date : min), dates[0]);
  const lastDate = dates.reduce((max, date) => (date.isAfter(max) ? date : max), dates[0]);

  return lastDate.diff(firstDate, 'day');
};

/**
 * Three or more slots, or slots spread over more than a week, already are a custom
 * pattern — the repeat step adds nothing, so go straight to confirmation.
 */
export const shouldSkipFrequency = (canonicalSlots = []) => {
  if (!canonicalSlots?.length) return false;
  if (canonicalSlots.length >= 3) return true;
  return getSlotDateRangeDays(canonicalSlots) >= 8;
};

// Only scheduled, completed and rescheduled classes count towards the student's
// progression on the course; every other status (no-show, cancelled…) is ignored.
const COUNTED_CLASS_STATUSES = ['SCHEDULED', 'COMPLETED', 'RESCHEDULED'];

/**
 * Next class number for the student on this course, derived from V2_APIS.myClassesData:
 * the highest classNumber among counted statuses, plus one. Returns 0 when there is
 * nothing to go on, so callers can fall back to their old numbering.
 */
export const getNextClassNumber = (classes = []) => {
  if (!Array.isArray(classes)) return 0;

  const highest = classes
    .filter((item) =>
      COUNTED_CLASS_STATUSES.includes(String(item?.classStatus).toUpperCase())
    )
    .reduce((max, item) => {
      const classNumber = Number(item?.classNumber);
      return Number.isFinite(classNumber) && classNumber > max ? classNumber : max;
    }, 0);

  return highest ? highest + 1 : 0;
};

export const formatClassLabel = (slot, index, startingClassNumber = 0) => {
  if (startingClassNumber) {
    return `Class ${String(startingClassNumber + index).padStart(2, '0')}`;
  }
  if (slot?.classNumber) {
    return `Class ${String(slot.classNumber).padStart(2, '0')}`;
  }
  return `Slot ${index + 1}`;
};

const formatSuccessSlotSummary = (slot, timezoneLabel) => {
  const date = dayjs(slot.slotDateInTimeZone, 'DD/MM/YYYY').format('dddd, Do MMMM');
  const time = dayjs(slot.slotStartTimeInTimeZone, 'HH:mm').format('h:mm A');
  return `${date}, ${time} (${timezoneLabel})`;
};

export const formatSuccessSlotLine = (slots = [], timezoneLabel) => {
  if (!slots.length) return '';
  const firstSlotSummary = formatSuccessSlotSummary(slots[0], timezoneLabel);
  if (slots.length === 1) return firstSlotSummary;
  return `${firstSlotSummary} + ${slots.length - 1} More`;
};
